"use client"

import { LockIcon } from "lucide-react"

import { cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"

/**
 * A report that cannot be run, explained — instead of the failure it used to be.
 *
 * A branch-dimension report ("Dépenses par branche", "Solde net par branche", …) breaks results down by
 * branch, so under `BRANCH_AUTONOMY` it would state the absolute figures of branches the reader may not see.
 * The server refuses it, correctly, with `refuseBranchDimensionTemplate`.
 *
 * The refusal arrived as a red "could not load" card with a Retry that could never succeed — the fourth place
 * this pattern appeared, after `$0.00`, removed widgets and the two Branch Details cards. A refusal is a
 * STATE, not an error: nothing went wrong, and nothing the reader does will change it.
 *
 * Deliberately not offering a retry, and deliberately naming the reason rather than a generic "no access":
 * the reader is usually the person who chose the governance mode, and "some branches keep their own books"
 * connects the wall to the decision that put it there.
 */
export function ReportLockedNotice({ reasonKey }: { reasonKey?: string | null }) {
  const t = useTranslations()

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg px-6 py-14 text-center",
        /*
         * A faint fill rather than an outline on the page's own background.
         *
         * Dashed-border-on-nothing reads as a placeholder — something that failed to arrive. This state is
         * the opposite: the report is deliberately withheld, and a filled panel says the surface is settled.
         * Muted rather than toned, because a colour would pull it back toward looking like a warning.
         */
        "border-border/60 bg-muted/30 border",
      )}
    >
      <span className='bg-background text-muted-foreground ring-border/60 inline-flex size-10 items-center justify-center rounded-full ring-1'>
        <LockIcon className='size-4' aria-hidden />
      </span>
      <div className='space-y-1'>
        <p className='text-foreground text-sm font-medium'>{t("finance.reports.locked.title" as never)}</p>
        <p className='text-muted-foreground max-w-md text-xs'>
          {/* The server's own reason when it sent one; the generic explanation otherwise. */}
          {t((reasonKey ?? "finance.reports.locked.description") as never)}
        </p>
      </div>
    </div>
  )
}
